import { Controller } from "@hotwired/stimulus"
import { forceSetValueOnSelect, isEmpty } from "./../helpers"

export default class extends Controller {
  static targets = ["taxField", "exemptTaxField", "showExemptTaxField", "naTaxKind", "naTaxKindRadios", "naTaxKindInnerWrapper", "npTaxKindInner", "technicalTaxField", "technicalTaxWrapper"]

  static values = {
    taxVisible: Boolean,
    tax2Visible: Boolean,
    naTaxKinds: Array,
    exemptTaxKinds: Array,
    technicalTaxOptions: Array,
    technicalTaxKindVisible: Boolean,
  }

  static outlets = ["invoice"]

  defaultTax
  lockedReasons = new Set()

  connect() {
    this.defaultTaxOpts = Array.from(this.hasTaxFieldTarget && this.taxFieldTarget.options ? this.taxFieldTarget.options : []).map(option => option.cloneNode(true))

    if (this.hasExemptTaxKindsValue) this.updateExemptTaxVisibility()
    this.updateOoAvailability()
    this.zeroTaxAlert()
    this.captureTechnicalTax()
  }

  /**
   * blokuje automatyczne nadpisywanie stawek (np. domyślną stawką z działu) na czas trwania
   * powodu blokady — kilka niezależnych powodów (np. odwrotne obciążenie, transakcja trójstronna)
   * może być aktywnych naraz, więc śledzimy je osobno zamiast pojedynczej flagi bool
   * @param {string} reason
   */
  lockTax(reason) {
    this.lockedReasons.add(reason)
  }

  /** @param {string} reason */
  unlockTax(reason) {
    this.lockedReasons.delete(reason)
  }

  get locked() {
    return this.lockedReasons.size > 0
  }

  /**
   * twardo wymusza JEDNĄ, niewybieralną stawkę na wszystkich pozycjach (np. dla zablokowanego
   * odwrotnego obciążenia) — w odróżnieniu od setTaxOptions/moss NIE zachowuje poprzedniej
   * wartości jako dodatkowej opcji, tak samo jak robi to already des_controller#hideTaxes
   * @param {string} value
   */
  lockTaxTo(value) {
    this.invoiceOutlet.invoicePositionCalculatorOutlets.forEach((position) => {
      position.taxFieldTargets.forEach((taxField) => {
        if (taxField.matches("select")) {
          taxField.replaceChildren() // czyścimy opcje, forceSetValueOnSelect dołoży jedną z `value`
        } else {
          taxField.disabled = true
        }
        forceSetValueOnSelect(taxField, value)
        taxField.dispatchEvent(new Event("change"))
      })
    })
  }

  captureTechnicalTax() {
    const technicalTaxFields = this.technicalTaxFieldTargets // target getter nie jest cache'owany — czytamy raz, nie w pętli

    this.taxFieldTargets.forEach((taxField, index) => {
      const technicalTaxField = technicalTaxFields[index]
      if (!technicalTaxField) return
      if (this.technicalTaxOptionsValue.includes(taxField.value)) {
        technicalTaxField.value = taxField.value
      }
    })
  }

  updateTechnicalTaxVisibility() {
    const rcChecked = this.invoiceOutlet.hasInvoiceReverseChargeOutlet && this.invoiceOutlet.invoiceReverseChargeOutlet.checkboxTarget.checked
    const show = this.technicalTaxKindVisibleValue || rcChecked
    this.technicalTaxWrapperTargets.forEach((wrapper) => wrapper.classList.toggle("hidden", !show))
  }

  /** @param {HTMLOptionElement[]} options  */
  setTaxOptions(options) {
    let allTaxesMatched = true
    this.invoiceOutlet.invoicePositionCalculatorOutlets.forEach((position) => {
      allTaxesMatched &&= position.setTaxOptions(options)
    })
    this.invoiceOutlet.invoiceInlineEstimateOutlets.forEach((ie) => ie.setTaxOptions(options))
    return allTaxesMatched
  }

  /**
   * przywraca możliwe stawki podatku z momentu załadowania strony
   */
  setDefaultTaxes() {
    this.setTaxOptions(this.defaultTaxOpts)
    this.invoiceOutlet.invoicePositionCalculatorOutlets.forEach((p) => p.restorePreMossTaxes())
  }

  /**
   * przywraca pełną listę stawek po odznaczeniu odwrotnego obciążenia
   */
  restoreDefaultTaxAfterReverseCharge() {
    const hasNp = this.defaultTaxOpts.some((option) => option.value === "np")
    const fallback = this.defaultTaxOpts.find((option) => option.value !== "oo") ?? this.defaultTaxOpts[0]
    const restoredValue = hasNp ? "np" : (fallback?.value ?? "")

    this.invoiceOutlet.invoicePositionCalculatorOutlets.forEach((position) => {
      position.taxFieldTargets.forEach((taxField) => {
        if (taxField.matches("select")) {
          taxField.replaceChildren(...this.defaultTaxOpts.map((option) => option.cloneNode(true)))
        } else {
          // pole przełączone na custom input (opcja "więcej...") — odblokowujemy z powrotem
          taxField.disabled = false
        }
        forceSetValueOnSelect(taxField, restoredValue)
        taxField.dispatchEvent(new Event("change"))
      })
    })
    this.updateOoAvailability()
  }

  /**
   * blokuje wybór "oo" gdy kraj nabywcy jest zagraniczny
   */
  updateOoAvailability() {
    if (!this.hasInvoiceOutlet || !this.invoiceOutlet.hasBuyerCountryTarget) return

    const disableOo = !["", "PL"].includes(this.invoiceOutlet.buyerCountryTarget.value)
    this.taxFieldTargets.forEach((taxField) => {
      if (taxField.id.includes("correction_before")) return
      const ooOption = Array.from(taxField.options).find((option) => option.value === "oo")
      if (ooOption) ooOption.disabled = disableOo
    })
  }

  /**
   * ręczny wybór "oo" bez zaznaczonego odwrotnego obciążenia auto-zaznacza jego checkbox.
   */
  checkOoTaxSelection(event) {
    if (event.target.value !== "oo") return

    const rc = this.invoiceOutlet.hasInvoiceReverseChargeOutlet ? this.invoiceOutlet.invoiceReverseChargeOutlet : null
    if (!rc || rc.checkboxTarget.checked) return

    rc.checkboxTarget.checked = true
    rc.checkboxTarget.dispatchEvent(new Event("change"))
  }

  /**
   * @param {string[]} options
   */
  isTaxMatchingAnywhere(options) {
    return this.taxFieldTargets.find((element) => options.includes(element.value)) !== undefined
  }

  isTaxNaAnywhere() {
    return this.isTaxMatchingAnywhere(this.naTaxKindsValue)
  }

  isTaxExemptAnywhere() {
    return this.isTaxMatchingAnywhere(this.exemptTaxKindsValue)
  }

  updateExemptTaxVisibility() {
    if (!this.hasExemptTaxFieldTarget) return

    const shouldShowExempt = (this.hasShowExemptTaxFieldTarget && this.showExemptTaxFieldTarget.checked) || this.isTaxExemptAnywhere()
    if (shouldShowExempt) {
      this.exemptTaxFieldTarget.classList.remove("hidden")
    } else {
      this.exemptTaxFieldTarget.classList.add("hidden")
    }
  }

  isMossOn() {
    return this.invoiceOutlet.hasMossOutlet && this.invoiceOutlet.mossOutlet.useMossTarget.checked
  }

  updateNaKindVisibility() {
    // TODO: if correction
    if (!this.isMossOn() && this.isTaxNaAnywhere()) {
      this.naTaxKindRadiosTarget.classList.remove("hidden")
      const notSpecifiedRadio = this.naTaxKindRadiosTarget.querySelector('input[value="not_specified"]') // nie istnieje na ksefie
      if (this.naTaxKindTarget.value === "" && notSpecifiedRadio !== null) {
        notSpecifiedRadio.checked = true
        notSpecifiedRadio.dispatchEvent(new Event("change"))
      }
    } else {
      this.naTaxKindRadiosTarget.classList.add("hidden")
      this.clearNaKind()
    }
  }

  clearNaKind() {
    this.naTaxKindTarget.value = ""
    this.naTaxKindRadiosTarget.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(elem => { elem.checked = false })
    this.naTaxKindInnerWrapperTarget.classList.add("hidden")
  }

  resetNpTaxKindInner() {
    if (!this.hasNaTaxKindInnerWrapperTarget) return

    const inner = this.naTaxKindInnerWrapperTarget.querySelector('input[type="checkbox"]')
    if (inner && inner.checked) {
      inner.checked = false
      inner.dispatchEvent(new Event("change"))
    }
  }

  selectExportServiceNaTaxKind() {
    if (!this.hasNaTaxKindRadiosTarget) return

    const exportServiceRadio = this.naTaxKindRadiosTarget.querySelector('input[value="export_service"]')
    if (exportServiceRadio && !exportServiceRadio.checked) {
      exportServiceRadio.checked = true
      exportServiceRadio.dispatchEvent(new Event("change"))
    }
  }

  /**
   * @param {boolean} disabled
   */
  setNpTaxKindInnerDisabled(disabled) {
    if (!this.hasNpTaxKindInnerTarget) return

    this.npTaxKindInnerTarget.disabled = disabled
    if (disabled && this.npTaxKindInnerTarget.checked) {
      this.npTaxKindInnerTarget.checked = false
      this.npTaxKindInnerTarget.dispatchEvent(new Event("change"))
    }
  }

  updateNaKind(event) {
    if (!this.isMossOn()) {
      const val = event.target.value
      if (val === "export_service_eu") {
        this.naTaxKindTarget.value = event.target.checked ? val : this.naTaxKindRadiosTarget.querySelector("input:checked").value
      } else {
        if (val === "export_service") {
          this.naTaxKindInnerWrapperTarget.classList.remove("hidden")
        } else {
          this.naTaxKindInnerWrapperTarget.classList.add("hidden")
          this.naTaxKindInnerWrapperTarget.querySelector("input").checked = false
        }
        this.naTaxKindTarget.value = val
      }
    }
  }

  zeroTaxAlert() {
    if (!this.hasInvoiceOutlet || !this.invoiceOutlet.hasBuyerCountryTarget || !this.invoiceOutlet.ksefEnabledValue) return

    const invoice = this.invoiceOutlet
    const hasSomeZeroTax = this.taxFieldTargets.some(elem => elem.value === "0")
    const buyerCountry = invoice.buyerCountryTarget.value
    if (hasSomeZeroTax && buyerCountry !== "" && buyerCountry !== "PL") {
      const isCompany = invoice.element.querySelector('#invoice_buyer_company_true')?.checked
      const isEuBuyer = invoice.euCountriesValue.includes(buyerCountry)

      if (isCompany && isEuBuyer && invoice.kindValue !== "wdt") {
        alert(invoice.translationsValue["invoice.could_be_wdt"])
      } else if (isCompany && !isEuBuyer && invoice.kindValue !== "export_products") {
        alert(invoice.translationsValue["invoice.could_be_export_products"])
      } else if (invoice.accountUseMossValue && !this.isMossOn()) {
        alert(invoice.translationsValue["invoice.could_be_vat_oss"])
      }
    }
  }

  get autocompleteTax() {
    if (this.locked) return false // np. zablokowane odwrotne obciążenie — nie nadpisujemy stawki wyborem produktu/działu

    const invoice = this.invoiceOutlet
    return !(invoice.hasDesOutlet && invoice.desOutlet.useDesTarget.checked)
  }

  fillWithDefaultTax(position = null) {
    if (this.locked) return

    const invoice = this.invoiceOutlet
    // Wymuszona stawka (RR: ryczałt) pomija autocompleteTax, bo select oferuje wtedy wyłącznie ją
    // (Invoice#custom_tax_options) — nie ma wyboru użytkownika, który dałoby się nadpisać.
    if (this.taxVisibleValue && invoice.forcedPositionTaxValue) {
      if (position == null) return // indeks 0 jest falsy, więc null/undefined testujemy jawnie

      const taxField = document.getElementById(`invoice_positions_attributes_${position}_tax`)
      if (!taxField) return

      forceSetValueOnSelect(taxField, invoice.forcedPositionTaxValue)
      taxField.dispatchEvent(new Event("change"))
      return
    }
    if (this.taxVisibleValue && !["wdt", "export_products"].includes(invoice.kindValue) && this.autocompleteTax) {
      this.setDefaultTax()
      if (this.defaultTax === undefined || this.defaultTax === null || this.defaultTax === "") { return }
      if (position) {
        forceSetValueOnSelect(document.getElementById(`invoice_positions_attributes_${position}_tax`), this.defaultTax)
        document.getElementById(`invoice_positions_attributes_${position}_tax`).dispatchEvent(new Event("change"))
      } else {
        if (confirm(invoice.translationsValue["default_tax_all_confirm"])) {
          document.querySelectorAll("[id^=invoice_positions_attributes_][id$='_tax']").forEach((elem) => {
            forceSetValueOnSelect(elem, this.defaultTax)
            elem.dispatchEvent(new Event("change"))
          })
        }
      }
    }
  }

  /**
   * ustawia domyślny podatek z działu/firmy
   */
  setDefaultTax() {
    const invoice = this.invoiceOutlet
    if (invoice.hasDepartmentSelectTarget) {
      const deptId = invoice.departmentSelectTarget.value
      const deptAttributes = invoice.departmentsValue[deptId] ?? {}
      if (!isEmpty(deptAttributes["default_tax"]) && deptAttributes["force_tax_rate"] === "1") {
        this.defaultTax = deptAttributes["default_tax"]
      }
    }
  }

  /**
   * zmiana domyślnego podatku w pustych pozycjach po zmianie działu
   * @param {Object} deptAttributes
   */
  applyDepartmentDefaultTaxToPositions(deptAttributes) {
    if (!this.taxVisibleValue || this.locked) return

    this.applyDepartmentDefaultTax(deptAttributes["default_tax"], { emptyOnly: true })
  }

  /**
   * @param {Object} deptAttributes
   */
  forceFrDepartmentTaxIfReverseChargeOff(deptAttributes) {
    if (this.frForceTaxBlocked(deptAttributes)) return

    this.applyDepartmentDefaultTax(deptAttributes["default_tax"], { emptyOnly: false })
  }

  /**
   * @param {string} defaultTax
   * @param {{emptyOnly: boolean}} opts — emptyOnly: true = nie nadpisuj już wpisanej stawki
   * (tylko dołóż opcję do wyboru), false = wymuś zawsze i powiadom o zmianie
   */
  applyDepartmentDefaultTax(defaultTax, { emptyOnly }) {
    // na dokumencie z wymuszoną stawką (RR: ryczałt) stawka działu nie może jej nadpisać
    // ani dołożyć się do selecta jako kolejna opcja — por. Invoice#forced_position_tax
    const rate = this.invoiceOutlet.forcedPositionTaxValue || defaultTax

    this.invoiceOutlet.invoicePositionCalculatorOutlets.forEach((position) => {
      if (!position.hasTaxFieldTarget) return
      const taxField = position.taxFieldTarget

      if (!emptyOnly || position.isEmpty()) {
        forceSetValueOnSelect(taxField, rate)
        if (!emptyOnly) taxField.dispatchEvent(new Event("change"))
      } else if (!Array.from(taxField.options).some((option) => option.value === rate)) {
        const newOption = document.createElement("option")
        newOption.value = newOption.innerText = rate
        taxField.append(newOption)
      }
    })
  }

  /**
   * @param {HTMLSelectElement} taxField
   * @param {Object|undefined} dept
   */
  forceFrTaxOnNewPosition(taxField, dept) {
    if (this.frForceTaxBlocked(dept)) return

    taxField.value = dept["default_tax"]
  }

  /**
   * @param {Object|undefined} dept
   * @returns {boolean}
   */
  frForceTaxBlocked(dept) {
    if (this.invoiceOutlet.localeValue !== "fr" || dept?.["force_tax_rate"] !== "1") return true
    return !!document.getElementById("invoice_reverse_charge")?.checked
  }
}
